<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="10">
					<v-toolbar-title>{{ $t('add-ccm-record') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pa-4 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-form ref="form">
				<v-row dense class="ma-0">
					<v-col cols="12">
						<v-select
							:label="$t('type')"
							v-model="selectedType"
							:items="ccmTypes"
							item-text="name"
							:item-value="'value'"
							outlined
							hide-details
							:menu-props="{ maxHeight: 199, offsetY: true }" />
					</v-col>
					<v-col cols="12">
						<v-select
							:label="$t('provider')"
							v-model="selectedProvider"
							:items="doctors"
							item-text="fullName"
							:item-value="'id'"
							outlined
							hide-details
							:menu-props="{ maxHeight: 199, offsetY: true }" />
					</v-col>
					<v-col cols="12">
						<v-select
							:label="$t('coordinator')"
							v-model="selectedCoordinator"
							:items="nurses"
							item-text="fullName"
							:item-value="'id'"
							outlined
							hide-details
							:menu-props="{ maxHeight: 199, offsetY: true }" />
					</v-col>
					<v-col cols="12">
						<v-menu
							ref="dateMenu"
							v-model="dateMenu"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="auto">
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									:value="formattedDate"
									:label="$t('date-of-service')"
									prepend-inner-icon="mdi-calendar"
									readonly
									outlined
									hide-details
									v-bind="attrs"
									v-on="on" />
							</template>
							<v-date-picker v-model="dateOfService" no-title @input="dateMenu = false" />
						</v-menu>
					</v-col>

					<v-col cols="12" v-if="!doctors?.length">
						<v-card color="primary" class="pa-2 white--text">
							<v-icon size="19" color="white">mdi-alert-circle-outline</v-icon>
							{{ $t('note') }}: {{ $t('please-assign-a-doctor-to-the-patient') }}
						</v-card>
					</v-col>
					<v-col cols="12" v-if="!nurses?.length">
						<v-card color="primary" class="pa-2 white--text">
							<v-icon size="19" color="white">mdi-alert-circle-outline</v-icon>
							{{ $t('note') }}: {{ $t('please-assign-a-nurse-to-the-patient') }}
						</v-card>
					</v-col>

					<!-- <v-col cols="12"> -->
					<!-- 	<v-menu -->
					<!-- 		ref="timeMenu" -->
					<!-- 		v-model="timeMenu" -->
					<!-- 		:close-on-content-click="false" -->
					<!-- 		transition="scale-transition" -->
					<!-- 		offset-y -->
					<!-- 		max-width="290px" -->
					<!-- 		min-width="290px"> -->
					<!-- 		<template v-slot:activator="{ on, attrs }"> -->
					<!-- 			<v-text-field -->
					<!-- 				:value="formattedTime" -->
					<!-- 				:label="$t('time-of-service')" -->
					<!-- 				prepend-inner-icon="mdi-clock-time-four-outline" -->
					<!-- 				readonly -->
					<!-- 				outlined -->
					<!-- 				hide-details -->
					<!-- 				v-bind="attrs" -->
					<!-- 				v-on="on"></v-text-field> -->
					<!-- 		</template> -->
					<!-- 		<v-time-picker v-model="timeOfService" format="ampm" /> -->
					<!-- 	</v-menu> -->
					<!-- </v-col> -->
				</v-row>
			</v-form>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:dense="false"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip top v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add-record') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addRecord()" color="dark_purple" style="color: white" block>{{ $t('add-record') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { formatTimestamp } from '@/utils/utilities';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMRecordAdd',
	data () {
		return {
			doctors: null,
			nurses: null,
			selectedCoordinator: null,
			selectedProvider: null,
			selectedType: null,
			staff: null,
			timeSpentSeconds: 0,
			dateOfService: new Date().toISOString().substring(0, 10),
			dateMenu: false,
			timeMenu: false,
		};
	},
	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			userData: (state) => state.authentication.userData,
		}),
		addButtonEnabled () {
			return this.selectedCoordinator && this.selectedProvider && this.selectedType && this.timeSpentSeconds;
		},
		ccmTypes () {
			return [
				{
					name: this.$t('complex'),
					value: 'complex',
				},
				{
					name: this.$t('non-complex'),
					value: 'non-complex',
				},
				{
					name: this.$t('physician-npp-provided'),
					value: 'physician-npp-provided',
				},
			];
		},
		formattedDate () {
			return this.formatTimestamp(this.dateOfService);
		},
	},
	methods: {
		...mapActions({
			addCCMRecord: 'ccmRecords/addCCMRecord',
			getCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),
		formatTimestamp,
		async addRecord () {
			const request = {
				patientId: this.detailedUser.userData.id,
				hospitalId: localStorage.getItem('selectedHospitalAssignedId'),
				dateOfService: this.dateOfService,
				providerId: this.selectedProvider,
				coordinatorId: this.selectedCoordinator,
				isNonComplex: this.selectedType === 'non-complex',
				isComplex: this.selectedType === 'complex',
				isPhysicianNPPProvidedCCM: this.selectedType === 'physician-npp-provided',
				createdBy: `${this.userData.firstName} ${this.userData.lastName}`,
				lastModifiedBy: `${this.userData.firstName} ${this.userData.lastName}`,
				timeSpentSeconds: this.timeSpentSeconds,
			};
			await this.addCCMRecord(request);
			this.closeDrawer();
		},
		closeDrawer () {
			this.selectedCoordinator = null;
			this.selectedProvider = null;
			this.selectedType = null;
			this.timeSpentSeconds = 0;
			this.dateMenu = false;
			this.timeMenu = false;
			this.dateOfService = new Date().toISOString().substring(0, 10);
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		filterDoctors () {
			return this.staff.filter((member) => member.roleId === 3 && member.status === 1);
		},
		filterNurses () {
			return this.staff.filter((member) => member.roleId === 4 && member.status === 1);
		},
		getStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList.map((member) => ({
				...member,
				fullName: `${member.firstName} ${member.lastName}`,
			}));
		},
	},
	async created () {
		this.staff = await this.getStaff();
		this.doctors = await this.filterDoctors();
		this.nurses = await this.filterNurses();
	},

	watch: {
		detailedUser: {
			handler (newVal) {
				if (newVal && newVal.userData) {
					this.staff = this.getStaff();
					this.doctors = this.filterDoctors();
					this.nurses = this.filterNurses();
				}
			},
			immediate: true,
		},
	},
	components: { InputTimeFormatter },
};
</script>
